import type { Moment } from 'moment';
import type { AnalyticsFilterBody } from 'actions/analyticActions';

export interface AnalyticsAppliedFilters extends AnalyticsFilterBody {
  active: Moment[];
  expected: Moment[];
  created: Moment[];
  from_date: Moment;
  to_date: Moment;
  search: string;
}

export interface AnalyticsDashboardContextValue {
  appliedFilters: AnalyticsAppliedFilters;
}

export interface AnalyticsWidgetOption {
  label: string;
  value: number | string;
}

export interface AnalyticsWidgetConfig {
  title: string;
  total: number;
  simpleView: boolean;
  mainInfoOnly?: boolean;
  description: string;
  options: AnalyticsWidgetOption[];
  loading: boolean;
  empty: boolean;
  extra?: number;
  extra_description?: string;
}

export interface AnalyticsTooltipDatum {
  name: string;
  data: string | number;
}

export interface AnalyticsTooltipOption {
  name: string;
  data: AnalyticsTooltipDatum[];
}

export type AnalyticsTooltipOptions =
  | AnalyticsTooltipOption[]
  | { bar: AnalyticsTooltipOption[]; line: AnalyticsTooltipOption[] };

export type AnalyticsTooltipChartType = 'doughnut' | string;

export interface AnalyticsChartTooltipContext {
  chart: {
    canvas: HTMLCanvasElement & { parentNode: ParentNode & { querySelector: typeof document.querySelector } };
  };
  tooltip: {
    opacity: number;
    body?: Array<{ lines: string[] }>;
    dataPoints: Array<{ label: string }>;
    title: string[];
    caretX: number;
    caretY: number;
  };
}

export interface RejectionNumberRawItem {
  type: string;
  rejections: number;
  absolute_conversion: number;
}

export interface RejectionReasonRawItem {
  type: string;
  reasons: Array<{
    reason: string;
    rejections: number;
    absolute_conversion: number;
  }>;
}

export interface TransitionStepRawResponse {
  steps_transition: Array<{
    type: string;
    candidates_count: number;
    relative_conversion?: number | null;
    absolute_conversion?: number | null;
  }>;
  interacted: {
    new_interaction: number;
  };
}

export interface PassingStepRawItem {
  type: string;
  duration_hours: number;
}

export interface InteractedRawData {
  new_interaction: number;
  new_added: number;
}

export interface AssignedRawData {
  candidates: number;
  services: number;
  jobs: Array<{ name: string; candidates_number: number }>;
}

export interface JobOffersRawData {
  total: number;
  rejected: number;
  onboarding: number;
  waiting_feedback: number;
}

export interface TotalUniqueCandidatesRawData {
  candidates: number;
  services: number;
  jobs: unknown[];
}

export interface AnalyticsFilterListItem {
  uuid: string;
  name: string;
  company_name?: string;
}

export interface AnalyticsDashboardProps {
  defaultServices: AnalyticsFilterBody['services'];
}

export interface ChartWidgetState<T = unknown> {
  options: T;
  loading: boolean;
  empty?: boolean;
}

export const createInitialWidgetState = <T>(options: T): ChartWidgetState<T> => ({
  options,
  loading: true,
});
